// Error message translations and utilities for user-friendly error handling

export interface ErrorMessageConfig {
  title: string;
  message: string;
  suggestion?: string;
  action?: string;
}

// File extraction error messages in Portuguese
export const fileExtractionErrors: Record<string, ErrorMessageConfig> = {
  'File contains too little text content. Minimum 50 words required.': {
    title: 'Conteúdo insuficiente',
    message: 'O arquivo enviado possui pouco conteúdo de texto. É necessário no mínimo 50 palavras para criar um deck de estudos.',
    suggestion: 'Adicione mais conteúdo ao arquivo ou combine informações de múltiplos tópicos antes de enviar novamente.',
    action: 'Enviar arquivo com mais conteúdo'
  },
  'No text content could be extracted from the file': {
    title: 'Não foi possível extrair texto',
    message: 'Não conseguimos extrair nenhum conteúdo de texto do arquivo enviado.',
    suggestion: 'Verifique se o arquivo não está corrompido e contém texto legível.',
    action: 'Tente converter o arquivo para PDF ou DOCX e envie novamente.'
  },
  'File size exceeds maximum allowed size': {
    title: 'Arquivo muito grande',
    message: 'O arquivo enviado excede o tamanho máximo permitido de 10MB.',
    suggestion: 'Reduza o tamanho do arquivo ou divida o conteúdo em arquivos menores.',
    action: 'Comprima o arquivo ou remova imagens desnecessárias.'
  },
  'Unsupported file type': {
    title: 'Tipo de arquivo não suportado',
    message: 'O formato do arquivo enviado não é suportado pelo sistema.',
    suggestion: 'Use apenas arquivos nos formatos: PDF, TXT, DOC ou DOCX.',
    action: 'Converta seu arquivo para um dos formatos suportados.'
  },
  'Failed to extract text from file': {
    title: 'Erro ao processar arquivo',
    message: 'Ocorreu um erro técnico ao tentar extrair o texto do arquivo.',
    suggestion: 'O arquivo pode estar corrompido ou em um formato não padrão.',
    action: 'Tente salvar o arquivo em um formato diferente ou use outro arquivo.'
  }
};

// General error messages for different contexts
export const generalErrors: Record<string, ErrorMessageConfig> = {
  'AI_CARD_LIMIT_REACHED': {
    title: 'Limite de cards atingido',
    message: 'Usuários gratuitos podem criar decks com até 20 cards usando IA.',
    suggestion: 'Faça upgrade para Premium para criar decks maiores ou reduza a quantidade de cards.',
    action: 'Ver planos Premium'
  },
  'DECK_LIMIT_REACHED': {
    title: 'Limite de decks atingido',
    message: 'Você atingiu o limite máximo de decks para sua conta gratuita.',
    suggestion: 'Faça upgrade para Premium para criar decks ilimitados.',
    action: 'Ver planos Premium'
  },
  'AI_MODULE_LIMIT_REACHED': {
    title: 'Limite de módulos com IA atingido',
    message: 'Usuários gratuitos podem gerar conteúdo com IA para até 3 módulos de estudo.',
    suggestion: 'Faça upgrade para Premium para gerar conteúdo ilimitado com IA.',
    action: 'Ver planos Premium'
  },
  'NETWORK_ERROR': {
    title: 'Erro de conexão',
    message: 'Não foi possível conectar com o servidor.',
    suggestion: 'Verifique sua conexão com a internet e tente novamente.',
    action: 'Tentar novamente'
  },
  'SERVER_ERROR': {
    title: 'Erro interno do servidor',
    message: 'Ocorreu um erro interno no servidor.',
    suggestion: 'Tente novamente em alguns minutos. Se o problema persistir, entre em contato conosco.',
    action: 'Tentar novamente'
  }
};

/**
 * Get user-friendly error message configuration
 */
function getErrorText(error: any): string {
  const candidates: any[] = [];

  if (typeof error === 'string') {
    candidates.push(error);
  }

  if (error?.response?.data?.message) {
    candidates.push(error.response.data.message);
  }

  if (error?.response?.data?.error) {
    candidates.push(error.response.data.error);
  }

  if (error?.message) {
    candidates.push(error.message);
  }

  const flattened = candidates
    .flatMap((value) => {
      if (Array.isArray(value)) {
        return value.filter((item): item is string => typeof item === 'string');
      }
      return typeof value === 'string' ? [value] : [];
    })
    .join(' ');

  return flattened || '';
}

export function getErrorMessage(error: any): ErrorMessageConfig {
  const messageText = getErrorText(error);

  // Handle API response errors
  if (error?.response?.data?.message || error?.response?.data?.error || error?.message) {
    const apiMessage = error.response?.data?.message || error.response?.data?.error || error.message;
    const normalizedApiMessage = Array.isArray(apiMessage) ? apiMessage.join(' ') : apiMessage;
    
    // Check for file extraction errors
    for (const [key, config] of Object.entries(fileExtractionErrors)) {
      if (messageText.includes(key) || normalizedApiMessage === key || (typeof normalizedApiMessage === 'string' && normalizedApiMessage.includes(key))) {
        return config;
      }
    }
    
    // Check for error codes
    if (error.response?.data?.code && generalErrors[error.response.data.code]) {
      return generalErrors[error.response.data.code];
    }
    
    // Handle HTTP status codes
    switch (error.response?.status) {
      case 400:
        return {
          title: 'Dados inválidos',
          message: normalizedApiMessage || messageText || 'Dados inválidos.',
          suggestion: 'Verifique os dados enviados e tente novamente.'
        };
      case 401:
        return {
          title: 'Não autorizado',
          message: 'Você precisa estar logado para realizar esta ação.',
          action: 'Fazer login'
        };
      case 403:
        return {
          title: 'Acesso negado',
          message: 'Você não tem permissão para realizar esta ação.',
          suggestion: 'Entre em contato com o suporte se acredita que isso é um erro.'
        };
      case 404:
        return {
          title: 'Não encontrado',
          message: 'O recurso solicitado não foi encontrado.',
          suggestion: 'Verifique se o link está correto ou tente novamente.'
        };
      case 429:
        return {
          title: 'Muitas tentativas',
          message: 'Você fez muitas tentativas. Aguarde um momento antes de tentar novamente.',
          suggestion: 'Aguarde alguns minutos antes de fazer uma nova tentativa.'
        };
      case 500:
        return generalErrors.SERVER_ERROR;
      default:
        return {
          title: 'Erro inesperado',
          message: normalizedApiMessage || messageText || 'Ocorreu um erro inesperado.',
          suggestion: 'Tente novamente. Se o problema persistir, entre em contato conosco.'
        };
    }
  }
  
  // Handle network errors
  if (error?.code === 'NETWORK_ERROR' || error?.message === 'Network Error') {
    return generalErrors.NETWORK_ERROR;
  }
  
  // Handle timeout errors
  if (error?.code === 'ECONNABORTED' || error?.message?.includes('timeout')) {
    return {
      title: 'Tempo limite excedido',
      message: 'A operação demorou mais do que o esperado.',
      suggestion: 'Tente novamente. Arquivos grandes podem demorar mais para processar.',
      action: 'Tentar novamente'
    };
  }
  
  // Default error message
  return {
    title: 'Erro inesperado',
    message: error?.message || 'Ocorreu um erro inesperado.',
    suggestion: 'Tente novamente. Se o problema persistir, entre em contato com nosso suporte.',
    action: 'Tentar novamente'
  };
}

/**
 * Format error message for display
 */
export function formatErrorMessage(error: any): string {
  const config = getErrorMessage(error);
  
  let message = config.message;
  
  if (config.suggestion) {
    message += ` ${config.suggestion}`;
  }
  
  return message;
}

/**
 * Get error title for display
 */
export function getErrorTitle(error: any): string {
  const config = getErrorMessage(error);
  return config.title;
}

/**
 * Get error action text
 */
export function getErrorAction(error: any): string | undefined {
  const config = getErrorMessage(error);
  return config.action;
}

/**
 * Check if error is related to subscription limits
 */
export function isSubscriptionLimitError(error: any): boolean {
  const code = error?.response?.data?.code;
  return code === 'AI_CARD_LIMIT_REACHED' || code === 'DECK_LIMIT_REACHED';
}

/**
 * Check if error is a file-related error
 */
export function isFileError(error: any): boolean {
  const message = getErrorText(error);
  return Object.keys(fileExtractionErrors).some(key => 
    message.includes(key) || message === key
  );
}